import React, { useState, useRef, useEffect } from 'react';
import '../css/selectField.css';
import { ChevronIcon, SearchIcon } from '../../../../../asset-controller-mod/asset-serv/icons/iconExporter';

export type SelectValue = string | number;

export interface SelectOption {
  value: SelectValue;
  label: string;
  icon?: React.ReactNode;
}

export interface SelectFieldProps {
  id?: string;
  name?: string;
  value?: SelectValue;
  placeholder?: string;
  options: SelectOption[];
  onChange?: (value: SelectValue) => void;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  searchable?: boolean;
  assistiveText?: string;
  error?: boolean;
  errorText?: string;
  warning?: boolean;
}

const SelectFieldNoLabel: React.FC<SelectFieldProps> = ({
  id,
  name,
  value,
  placeholder,
  options,
  onChange,
  required = false,
  disabled = false,
  className = '',
  searchable = false,
  assistiveText,
  error = false,
  errorText,
  warning = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  const keyOf = (v: SelectValue | undefined) => String(v ?? "");

  const selectedOption = options.find(opt => keyOf(opt.value) === keyOf(value));
  const hasValue = value !== undefined && value !== null && String(value).length > 0;

  const prettyValue = (v: unknown) => {
    const s = String(v ?? /* istanbul ignore next */ ""); //NOSONAR
    /* istanbul ignore next */
    if (!s) return "";
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const displayLabel = selectedOption?.label
    ?? (hasValue ? prettyValue(value) : undefined)
    ?? placeholder;

  const filteredOptions = searchable
    ? options.filter(option =>
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    )
    : options;

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleToggle = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const handleSelect = (optionValue: SelectValue, e: React.MouseEvent) => {
    e.stopPropagation();
    if (onChange) {
      onChange(optionValue);
    }
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  return (
    <div
      ref={containerRef}
      className={`scl--cms-components-selectField-container ${className}`}
    >
      <div //NOSONAR
        className={`scl--cms-components-selectField-plate ${error ? 'scl--cms-components-selectField--error' : ''} ${warning ? 'scl--cms-components-selectField--warning' : ''} ${disabled ? 'scl--cms-components-selectField--disabled' : ''} ${isOpen ? 'scl--cms-components-selectField--open' : ''}`}
        onClick={handleToggle}
      >

        <div className="scl--cms-components-selectField-wrapper">
          <div className="scl--cms-components-selectField-valueWrapper">
            <div className="scl--cms-components-selectField-textWrapper">
              {hasValue && (
                <span className="scl--cms-components-selectField-text">
                  {selectedOption?.icon && (
                    <span className="scl--cms-components-selectField-selectedIcon">
                      {selectedOption.icon}
                    </span>
                  )}
                  {displayLabel}
                </span>
              )}
            </div>

            <ChevronIcon className="scl--cms-components-selectField-chevronIcon" />
          </div>
        </div>
      </div>

      <div className={`scl--cms-components-selectField-dropdown ${isOpen ? 'scl--cms-components-selectField-dropdown--open' : ''}`}>
        {searchable && (
          <div className="scl--cms-components-selectField-search">
            <SearchIcon className='scl--cms-components-selectField-searchIcon' />
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={handleSearchChange}
              className="scl--cms-components-selectField-searchInput"
              onClick={(e) => e.stopPropagation()}
              autoFocus={isOpen}
              autoComplete='off'
            />
          </div>
        )}

        <div className="scl--cms-components-selectField-options">
          {filteredOptions.length > 0 ? (
            filteredOptions.map((option) => (
              <div //NOSONAR
                key={option.value}
                className={`scl--cms-components-selectField-option ${keyOf(value) === keyOf(option.value) ? 'scl--cms-components-selectField-option--selected' : ''
                  }`}
                onClick={(e) => handleSelect(option.value, e)}
              >
                {option.icon && (
                  <span className="scl--cms-components-selectField-optionIcon">
                    {option.icon}
                  </span>
                )}
                <span className="scl--cms-components-selectField-optionLabel">
                  {option.label}
                </span>
              </div>
            ))
          ) : (
            <div className="scl--cms-components-selectField-noOptions">
              No options found
            </div>
          )}
        </div>
      </div>

      {(assistiveText || errorText) && (
        <div className={`scl--cms-components-selectField-assistive ${error ? 'scl--cms-components-selectField-assistive--error' : ''}`}>
          {error && errorText ? errorText : assistiveText}
        </div>
      )}

      <select
        id={id}
        name={name}
        value={keyOf(value)}
        onChange={(e) => {
          const picked = options.find((o) => keyOf(o.value) === e.target.value);
          onChange?.(picked ? picked.value : /* istanbul ignore next */  e.target.value);
        }}
        required={required}
        disabled={disabled}
        style={{ display: 'none' }}
      >
        <option value="">{placeholder}</option>
        {options.map((option) => (
          <option key={keyOf(option.value)} value={keyOf(option.value)}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
};

export default SelectFieldNoLabel;